import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Package, Download, Truck, RotateCcw, CornerUpLeft, Loader2, History, MapPinOff, AlertTriangle, KeyRound, Send, RefreshCw, Clock } from 'lucide-react';
import { downloadAuthedFile } from '../../utils/downloadAuthedFile';
// iter-235 SSOT — shipment_state matrix consulted for visibility
import { readShipmentState } from '../../lib/shipmentCtaMatrix';

/**
 * PipelineList — shared list renderer for the 6 progressive Pack & Ship tabs
 * (Forward / Pickup / Shipped / In-Transit / Delivered / NDR / RTO).
 *
 * Each tab passes its bucket of shipments and any stage-specific affordance
 * flags (e.g. showNdrActions). Cards render AWB + customer + destination +
 * cost + label download; NDR cards additionally expose Re-attempt and
 * Return-to-Sender (RTO) buttons.
 */
export default function PipelineList({
  stage,
  title,
  emptyText,
  items,
  formatPrice,
  showNdrActions = false,
  apiCall,
  onRefresh,
  toast,
}) {
  const [busyId, setBusyId] = useState(null);
  // NDR ledger: { [awb]: {reattempts, rtos, last_action, last_action_at, rows[]} }
  // Loaded once when the NDR tab mounts so each card can show its history.
  const [ndrLedger, setNdrLedger] = useState({});
  const [expandedAwb, setExpandedAwb] = useState(null);
  // iter-229d — Per-card OTP input + button-busy state for self-managed
  // shipments. Keys are shipment.id so multiple cards don't clash.
  const [otpInputs, setOtpInputs] = useState({});
  const [otpBusyId, setOtpBusyId] = useState(null);
  const [outForDelBusyId, setOutForDelBusyId] = useState(null);
  const [resendBusyId, setResendBusyId] = useState(null);

  const isSelfManaged = (sh) =>
    sh?.shipping_method === 'self_managed' || sh?.provider === 'self_managed';

  // ── SLA countdown helper ────────────────────────────────────────────────
  const getSlaRemaining = (sh) => {
    const claimedAt = sh?.delivery_claimed_at || sh?.out_for_delivery_at;
    if (!claimedAt) return null;
    const expiresAt = new Date(new Date(claimedAt).getTime() + 72 * 60 * 60 * 1000);
    const diffMs = expiresAt - Date.now();
    if (diffMs <= 0) return { expired: true, hoursLeft: 0, label: 'SLA Expired' };
    const hoursLeft = Math.floor(diffMs / 3_600_000);
    const minsLeft  = Math.floor((diffMs % 3_600_000) / 60_000);
    return {
      expired:  false,
      hoursLeft,
      label:    hoursLeft > 0 ? `${hoursLeft}h ${minsLeft}m left` : `${minsLeft}m left`,
      urgent:   hoursLeft < 6,
      warning:  hoursLeft < 12,
    };
  };

  const submitOutForDelivery = async (sh) => {
    if (!apiCall || !sh?.id) return;
    setOutForDelBusyId(sh.id);
    try {
      await apiCall(`/api/shipments/${sh.id}/out-for-delivery`, { method: 'POST' });
      toast?.({
        title: 'Marked out for delivery',
        description: '72-hour SLA window started. Customer will receive a WhatsApp confirmation request.',
      });
      onRefresh?.();
    } catch (err) {
      toast?.({
        title: 'Could not mark out for delivery',
        description: err?.message || 'Try again.',
        variant: 'destructive',
      });
    } finally {
      setOutForDelBusyId(null);
    }
  };

  const resendOtp = async (sh) => {
    if (!apiCall || !sh?.id) return;
    setResendBusyId(sh.id);
    try {
      await apiCall(`/api/shipments/${sh.id}/resend-delivery-otp`, { method: 'POST' });
      toast?.({
        title: 'OTP resent',
        description: 'A new delivery code was sent to the customer via WhatsApp.',
      });
    } catch (err) {
      toast?.({
        title: 'Resend failed',
        description: err?.message || 'Try again.',
        variant: 'destructive',
      });
    } finally {
      setResendBusyId(null);
    }
  };

  const submitOtp = async (sh) => {
    if (!apiCall || !sh?.id) return;
    const otp = (otpInputs[sh.id] || '').trim();
    if (!/^\d{4}$/.test(otp)) {
      toast?.({
        title: 'Enter a 4-digit OTP',
        description: 'The customer receives this code via WhatsApp from your delivery partner.',
        variant: 'destructive',
      });
      return;
    }
    setOtpBusyId(sh.id);
    try {
      const res = await apiCall(`/api/shipments/${sh.id}/verify-delivery-otp`, {
        method: 'POST',
        body: JSON.stringify({ otp }),
      });
      toast?.({
        title: 'Delivery confirmed',
        description: 'Customer OTP verified. Payout will release after the return window.',
      });
      setOtpInputs((prev) => ({ ...prev, [sh.id]: '' }));
      onRefresh?.();
      return res;
    } catch (err) {
      // Backend returns structured detail on 400 (attempts_remaining) and
      // 429 (admin-review locked). AuthContext flattens it into the
      // error.message for us.
      toast?.({
        title: 'OTP verification failed',
        description: err?.message || 'Try again.',
        variant: 'destructive',
      });
    } finally {
      setOtpBusyId(null);
    }
  };

  useEffect(() => {
    if (!showNdrActions || !apiCall || !items || items.length === 0) return;
    let alive = true;
    (async () => {
      try {
        const data = await apiCall('/api/delhivery/ndr/ledger?limit=200');
        if (!alive) return;
        const byAwb = {};
        (data?.counts_by_waybill || {}) && Object.entries(data.counts_by_waybill || {}).forEach(([awb, counts]) => {
          byAwb[awb] = { ...counts, rows: [] };
        });
        (data?.ledger || []).forEach((row) => {
          if (!row.waybill) return;
          const e = byAwb[row.waybill] || { reattempts: 0, rtos: 0, rows: [] };
          e.rows = e.rows || [];
          e.rows.push(row);
          byAwb[row.waybill] = e;
        });
        setNdrLedger(byAwb);
      } catch {}
    })();
    return () => { alive = false; };
  }, [showNdrActions, items, apiCall]);

  const downloadLabel = async (sh) => {
    if (!sh.label_url) {
      toast?.({
        title: 'Label not available',
        description: 'No label URL on this shipment.',
        variant: 'destructive',
      });
      return;
    }
    const res = await downloadAuthedFile(
      sh.label_url,
      `${sh.order_id || sh.id}_label.pdf`,
    );
    if (!res.ok) {
      toast?.({
        title: 'Could not download label',
        description: res.status === 401 ? 'Session expired — please refresh.' : (res.error || 'Try again.'),
        variant: 'destructive',
      });
    }
  };

  const handleNdrAction = async (sh, action) => {
    if (!apiCall) return;
    const confirmText =
      action === 'reattempt'
        ? `Re-attempt delivery for AWB ${sh.awb}?`
        : `Initiate RTO (return parcel to vendor) for AWB ${sh.awb}?`;
    if (!window.confirm(confirmText)) return;
    setBusyId(sh.id);
    try {
      // Provider-aware routing:
      //  - self_managed shipments → status PATCH on internal shipment record
      //  - Delhivery shipments    → POST /api/delhivery/ndr/action with the
      //    Delhivery-required action codes ('RE-ATTEMPT' / 'PICKUP_RESCHEDULE')
      let path;
      let body;
      let method;
      if (sh.provider === 'self_managed') {
        path = `/api/shipping/vendor/manual-awb/${sh.id}/status`;
        body = { status: action === 'reattempt' ? 'IN_TRANSIT' : 'RTO', note: `Vendor ${action} from NDR tab` };
        method = 'PATCH';
      } else {
        path = `/api/delhivery/ndr/action`;
        body = {
          waybill: sh.awb,
          action: action === 'reattempt' ? 'RE-ATTEMPT' : 'PICKUP_RESCHEDULE',
        };
        method = 'POST';
      }
      await apiCall(path, { method, body: JSON.stringify(body) });
      toast?.({
        title: action === 'reattempt' ? 'Re-attempt requested' : 'RTO initiated',
        description: `AWB ${sh.awb}`,
      });
      // Reload NDR ledger so the new action shows in the History panel
      try {
        const data = await apiCall('/api/delhivery/ndr/ledger?limit=200');
        const byAwb = {};
        Object.entries(data?.counts_by_waybill || {}).forEach(([awb, counts]) => {
          byAwb[awb] = { ...counts, rows: [] };
        });
        (data?.ledger || []).forEach((row) => {
          if (!row.waybill) return;
          const e = byAwb[row.waybill] || { reattempts: 0, rtos: 0, rows: [] };
          e.rows = e.rows || [];
          e.rows.push(row);
          byAwb[row.waybill] = e;
        });
        setNdrLedger(byAwb);
      } catch {}
      onRefresh?.();
    } catch (err) {
      toast?.({
        title: 'Action failed',
        description: err?.message || 'Try again.',
        variant: 'destructive',
      });
    } finally {
      setBusyId(null);
    }
  };

  if (!items || items.length === 0) {
    return (
      <Card className="p-4 md:p-8 text-center" data-testid={`pipeline-${stage}-empty`}>
        <Package className="h-12 w-12 mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-700">{title}</h3>
        <p className="text-gray-500 text-sm">{emptyText}</p>
      </Card>
    );
  }

  return (
    <div className="grid gap-3" data-testid={`pipeline-${stage}-list`}>
      {items.map((sh) => {
        const meta = sh._order || {};
        const isManual = isSelfManaged(sh);
        const canon = (sh.canonical_state || '').toLowerCase();
        const showOutForDelivery = isManual && canon === 'picked_up';
        const showOtpEntry = isManual && canon === 'in_transit' && !sh.delivery_disputed;
        const otpLocked = !!sh.delivery_otp_locked_for_admin_review;
        const attemptsUsed = Number(sh.delivery_otp_attempts || 0);
        const attemptsRemaining = Math.max(0, 3 - attemptsUsed);
        // iter-235 SSOT — Pipeline row CTAs read from the canonical
        // shipment_state stamped by the backend (or fall back to local
        // derivation if not yet). Each row exposes only the actions the
        // matrix allows for its state — same contract as the Order
        // Details page and the Pack & Ship row.
        const _ss = readShipmentState(sh);
        const canPrintLabel = _ss.ctas.includes('print_label');
        const canTrack      = _ss.ctas.includes('track_shipment');
        return (
          <Card key={sh.id} data-testid={`pipeline-${stage}-row-${sh.id}`}>
            <CardContent className="p-3 md:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {sh.awb && (
                      <span className="font-mono text-xs sm:text-sm font-semibold text-blue-700">{sh.awb}</span>
                    )}
                    <Badge variant="outline" className="text-[10px]">
                      {(sh.courier_name || sh.provider || 'carrier').toString().toUpperCase()}
                    </Badge>
                    {isManual && (
                      <Badge variant="outline" className="text-[10px] bg-purple-50 border-purple-200 text-purple-700">
                        Self-managed
                      </Badge>
                    )}
                    {sh.last_event && (
                      <Badge variant="outline" className="text-[10px] capitalize">
                        {sh.last_event.replace(/_/g, ' ')}
                      </Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 text-xs sm:text-sm text-gray-700">
                    <div className="truncate">
                      <span className="text-gray-500">Order: </span>
                      {meta.order_number || sh.order_id?.slice(0, 8) || '—'}
                    </div>
                    <div className="truncate">
                      <span className="text-gray-500">To: </span>
                      {meta.customer_name || '—'} {meta.destination_pincode ? `· ${meta.destination_pincode}` : ''}
                    </div>
                    <div>
                      <span className="text-gray-500">Cost: </span>
                      {sh.total_logistics_cost ? formatPrice(sh.total_logistics_cost) : '—'}
                    </div>
                  </div>
                  {/* Bad-address NDR banner — surfaces Delhivery's reason so
                      the vendor knows to fix the customer's address before
                      re-attempting. NSL codes WIA/NA = address issues. */}
                  {sh.last_ndr_is_address_issue && (
                    <div
                      className="mt-2 flex items-start gap-2 rounded border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800"
                      data-testid={`pipeline-ndr-bad-address-${sh.id}`}
                    >
                      <MapPinOff className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <div className="font-semibold">Delivery failed: bad / incomplete address</div>
                        <div className="text-rose-700">
                          {sh.last_ndr_instructions || 'Delhivery rider could not locate the address.'}
                          {sh.last_ndr_nsl_code ? <span className="ml-1 text-[10px] opacity-70">(NSL: {sh.last_ndr_nsl_code})</span> : null}
                        </div>
                        <div className="mt-1 text-[11px]">
                          Open the order → fix the customer phone/address → click "Re-attempt".
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Generic NDR reason banner (non-address) — still useful but lower priority */}
                  {!sh.last_ndr_is_address_issue && sh.last_ndr_instructions && (showNdrActions || stage === 'ndr') && (
                    <div
                      className="mt-2 flex items-start gap-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900"
                      data-testid={`pipeline-ndr-reason-${sh.id}`}
                    >
                      <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <div className="font-semibold">Carrier reason: {sh.last_ndr_instructions}</div>
                        {sh.last_ndr_nsl_code ? <div className="text-[10px] opacity-70">NSL: {sh.last_ndr_nsl_code}</div> : null}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 flex-shrink-0">
                  {canPrintLabel && sh.label_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadLabel(sh)}
                      data-testid={`pipeline-${stage}-label-${sh.id}`}
                    >
                      <Download className="h-4 w-4 mr-1" /> Label
                    </Button>
                  )}
                  {canTrack && sh.tracking_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(sh.tracking_url, '_blank')}
                      data-testid={`pipeline-${stage}-track-${sh.id}`}
                    >
                      <Truck className="h-4 w-4 mr-1" /> Track
                    </Button>
                  )}
                  {showNdrActions && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        title={ndrLedger[sh.awb] ? `${ndrLedger[sh.awb].reattempts} re-attempts · ${ndrLedger[sh.awb].rtos} RTOs` : 'No prior NDR actions'}
                        onClick={() => setExpandedAwb(expandedAwb === sh.awb ? null : sh.awb)}
                        data-testid={`pipeline-ndr-history-${sh.id}`}
                      >
                        <History className="h-4 w-4 mr-1" />
                        History {ndrLedger[sh.awb]?.rows?.length ? `(${ndrLedger[sh.awb].rows.length})` : ''}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busyId === sh.id}
                        onClick={() => handleNdrAction(sh, 'reattempt')}
                        data-testid={`pipeline-ndr-reattempt-${sh.id}`}
                      >
                        {busyId === sh.id ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />}
                        Re-attempt
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busyId === sh.id}
                        className="text-rose-700 border-rose-300 hover:bg-rose-50"
                        onClick={() => handleNdrAction(sh, 'rto')}
                        data-testid={`pipeline-ndr-rto-${sh.id}`}
                      >
                        <CornerUpLeft className="h-4 w-4 mr-1" /> Return to Sender
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* iter-229d — Self-managed verification UI block. Renders
                  the "Mark Out for Delivery" CTA on picked_up shipments
                  and the OTP entry input on in_transit shipments. */}
              {(showOutForDelivery || showOtpEntry || otpLocked || sh.delivery_disputed) && (
                <div
                  className="mt-3 pt-3 border-t border-purple-200 bg-purple-50 -mx-3 md:-mx-4 px-3 md:px-4 pb-2 rounded-b"
                  data-testid={`self-managed-actions-${sh.id}`}
                >
                  {/* SLA countdown badge */}
                  {(() => {
                    const sla = getSlaRemaining(sh);
                    if (!sla) return null;
                    return (
                      <div className={`flex items-center gap-1.5 text-xs mb-2 ${
                        sla.expired ? 'text-red-700' : sla.urgent ? 'text-red-600' : sla.warning ? 'text-amber-700' : 'text-gray-500'
                      }`}>
                        <Clock className="h-3.5 w-3.5 shrink-0" />
                        <span>
                          {sla.expired ? '⚠ SLA Expired — mark delivered or contact admin' : `72h SLA: ${sla.label}`}
                        </span>
                      </div>
                    );
                  })()}

                  {sh.delivery_disputed && (
                    <div className="flex items-center gap-2 text-xs text-rose-700 mb-2">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      <span>Customer disputed delivery on WhatsApp. Awaiting admin triage.</span>
                    </div>
                  )}
                  {otpLocked && (
                    <div className="flex items-center gap-2 text-xs text-rose-700 mb-2">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      <span>OTP locked after 3 wrong tries. Admin review required.</span>
                    </div>
                  )}
                  {showOutForDelivery && (
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="text-xs text-gray-700">
                        Hand the parcel to your courier? Customer's WhatsApp will be pinged for delivery confirmation.
                      </div>
                      <Button
                        size="sm"
                        disabled={outForDelBusyId === sh.id}
                        onClick={() => submitOutForDelivery(sh)}
                        data-testid={`out-for-delivery-${sh.id}`}
                      >
                        {outForDelBusyId === sh.id ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4 mr-1" />
                        )}
                        Mark Out for Delivery
                      </Button>
                    </div>
                  )}
                  {showOtpEntry && !otpLocked && (
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <div className="flex items-center gap-2 flex-1">
                          <KeyRound className="h-4 w-4 text-purple-700" />
                          <div className="text-xs text-gray-700 flex-1">
                            Customer reads a 4-digit OTP at the doorstep. Type it below:
                            <span className="ml-1 text-purple-700">{attemptsRemaining} {attemptsRemaining === 1 ? 'try' : 'tries'} left</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Input
                            inputMode="numeric"
                            maxLength={4}
                            placeholder="_ _ _ _"
                            className="w-24 font-mono text-center text-lg tracking-widest"
                            value={otpInputs[sh.id] || ''}
                            onChange={(e) => {
                              const cleaned = e.target.value.replace(/\D/g, '').slice(0, 4);
                              setOtpInputs((p) => ({ ...p, [sh.id]: cleaned }));
                            }}
                            data-testid={`otp-input-${sh.id}`}
                          />
                          <Button
                            size="sm"
                            disabled={otpBusyId === sh.id || (otpInputs[sh.id] || '').length !== 4}
                            onClick={() => submitOtp(sh)}
                            data-testid={`verify-otp-${sh.id}`}
                          >
                            {otpBusyId === sh.id ? (
                              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                              <KeyRound className="h-4 w-4 mr-1" />
                            )}
                            Verify OTP
                          </Button>
                        </div>
                      </div>
                      {/* Resend OTP button */}
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>Customer didn't receive the WhatsApp code?</span>
                        <button
                          className="flex items-center gap-1 text-purple-700 hover:text-purple-900 underline underline-offset-2 font-medium disabled:opacity-50"
                          disabled={resendBusyId === sh.id}
                          onClick={() => resendOtp(sh)}
                          data-testid={`resend-otp-${sh.id}`}
                        >
                          {resendBusyId === sh.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <RefreshCw className="h-3 w-3" />
                          )}
                          {resendBusyId === sh.id ? 'Sending…' : 'Resend OTP'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* NDR ledger drilldown — expanded only for the AWB the user clicked. */}
              {showNdrActions && expandedAwb === sh.awb && (
                <div
                  className="mt-3 pt-3 border-t border-gray-200 space-y-2 text-xs"
                  data-testid={`pipeline-ndr-history-panel-${sh.id}`}
                >
                  <div className="font-semibold text-gray-700">
                    NDR action history{' '}
                    {ndrLedger[sh.awb] && (
                      <span className="font-normal text-gray-500">
                        · {ndrLedger[sh.awb].reattempts} re-attempts · {ndrLedger[sh.awb].rtos} RTOs
                      </span>
                    )}
                  </div>
                  {(ndrLedger[sh.awb]?.rows?.length ?? 0) === 0 ? (
                    <div className="text-gray-500 italic">No prior NDR actions on this AWB.</div>
                  ) : (
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {ndrLedger[sh.awb].rows.map((r, idx) => (
                        <div
                          key={`${r.upl_id || idx}-${idx}`}
                          className="flex items-center justify-between bg-gray-50 rounded px-2 py-1.5"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {(r.action || '').toUpperCase() === 'RE-ATTEMPT' ? (
                              <RotateCcw className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" />
                            ) : (
                              <CornerUpLeft className="h-3.5 w-3.5 text-rose-600 flex-shrink-0" />
                            )}
                            <span className="font-mono text-[10px] truncate">{r.action}</span>
                            <Badge variant="outline" className="text-[10px]">
                              {r.status || 'submitted'}
                            </Badge>
                          </div>
                          <span className="text-gray-500 text-[10px] flex-shrink-0 ml-2">
                            {r.created_at ? new Date(r.created_at).toLocaleString() : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
